import axios from 'axios';
import { useEffect, useState } from 'react';
import { API_CONFIG } from '../config/api';

/**
 * Axios instance for api.internal. The API host is its own Cloudflare Access
 * application, so the browser must send its CF_Authorization cookie
 * (withCredentials) for Access to identify the staff user.
 */
export const internalApi = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export const API_SESSION_MISSING_EVENT = 'api-session-missing';
export const API_SESSION_RESTORED_EVENT = 'api-session-restored';

/** Cheap internal GET; opening it in a tab runs the Access login for the API host. */
export const API_SIGN_IN_URL = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.GET_PARTNERS;

export const openApiSignIn = () => {
  window.open(API_SIGN_IN_URL, '_blank', 'noopener');
};

/** Cheap internal GET used to check whether the API session is valid. */
export const probeApiSession = () => internalApi.get(API_CONFIG.ENDPOINTS.GET_PARTNERS);

/**
 * True when the call failed because there is no valid Access session for the API:
 * a 401/403, or an axios error with no response (a blocked cross-origin Access
 * login redirect surfaces as a network / CORS error).
 */
export const isApiSessionError = (err) => {
  if (!err || axios.isCancel(err)) return false;
  const status = err.response?.status;
  if (status === 401 || status === 403) return true;
  return Boolean(err.isAxiosError) && !err.response;
};

export const getApiErrorMessage = (err, fallback) => {
  if (isApiSessionError(err)) {
    return 'Sign in to the internal API (opens in a new tab), then retry';
  }
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) return serverMessage;
  // Plain (non-HTTP) errors carry their own user-facing message.
  if (err instanceof Error && !err.isAxiosError && err.message) return err.message;
  return fallback;
};

internalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isApiSessionError(error)) {
      window.dispatchEvent(new Event(API_SESSION_MISSING_EVENT));
    }
    return Promise.reject(error);
  }
);

/** Counter that increments each time the API session is restored; use it as an effect dependency to reload data. */
export function useApiSessionRestored() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const onRestored = () => setCount((c) => c + 1);
    window.addEventListener(API_SESSION_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(API_SESSION_RESTORED_EVENT, onRestored);
  }, []);
  return count;
}
